import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { DeviceService } from '@omni/services/device/device.service';
import { GlanceCardViewDataModel } from '@omni/models/glanceCardViewDataModel';
import { IndSimpleTooltipComponent } from '@omni/components/shared/ind-simple-tooltip/ind-simple-tooltip';



@Component({
  selector: 'glance-card',
  templateUrl: 'glance-card.html',
  styleUrls:['glance-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlanceCardComponent {

  @Input() viewData: GlanceCardViewDataModel;
  /**
   * Activity creates, rendered as their own tiles ahead of the tools and separated from them.
   * Same shape and same output as viewData.buttons — a host that passes nothing is unchanged.
   */
  @Input() quickActions: any[] = [];
  /** Renders the overflow trigger. Omitted entirely when nothing is left to offer (D16). */
  @Input() hasQuickActionOverflow = false;
  @Input() backButton?: { id: string, icon: string };
  @Input() nameAction?: { id: string, imgSrc: string, name: string, isDisabled?: boolean };
  @Output() back = new EventEmitter<string>();
  @Output() onControlClick = new EventEmitter<any>();
  @Output() quickActionOverflow = new EventEmitter<Event>();

  public userInitials:string;

  /** The one tool that acts on the field row rather than on the customer. */
  private static readonly PERSONALIZE_ID = 'personalize-quick-glance';

  /** Everything the host passed except personalize, which is rendered with the fields. */
  public get toolButtons(): GlanceCardViewDataModel['buttons'] {
    return (this.viewData?.buttons || []).filter(button => button.id !== GlanceCardComponent.PERSONALIZE_ID);
  }

  public get personalizeButton(): GlanceCardViewDataModel['buttons'][0] | undefined {
    return (this.viewData?.buttons || []).find(button => button.id === GlanceCardComponent.PERSONALIZE_ID);
  }

  /**
   * The model carries four fixed slots, of which a host may fill any subset. Empty ones are
   * dropped so the grid packs rather than leaving a hole where a slot was.
   */
  public get fields(): { label: string, value: string }[] {
    const data = this.viewData;
    if (!data) return [];
    return [
      { label: data.thirdTextLabel, value: data.thirdText },
      { label: data.fourthTextLabel, value: data.fourthText },
      { label: data.fifthTextLabel, value: data.fifthText },
      { label: data.sixthTextLabel, value: data.sixthText },
    ].filter(field => field.label || field.value);
  }

  // Off on native: a tap fires the hover tooltip, which then lingers over the card.
  readonly showTooltips = !(this.device.isAndroid() || this.device.isIOS());

  constructor(private popoverCtrl: PopoverController, private device: DeviceService, private cd: ChangeDetectorRef) {}

  public ngOnInit() {
    this.userInitials = this.viewData && this.viewData.name ? this.getInitials(this.viewData.name) : '';
  }

  private getInitials(name) {
    if (!name) return '';
    const parts = name.trim().split(' '); 
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  public onButtonClick(button,event){
    if(button && !button.isDisabled) {
      let obj = {
        id: button.id,
        item: button,
        event: event,
      };
      this.onControlClick.emit(obj);
    }
  }

  // Not [disabled]: a disabled tile still has to reach the host, which tells the rep why.
  public onQuickActionClick(action, event) {
    this.onControlClick.emit({ id: action.id, item: action, event });
  }

  onExpandClick (){
    this.viewData.isExpanded = !this.viewData.isExpanded;
  }

  public async showFullTextIfTruncated(event: MouseEvent) {
    const textElement = event.currentTarget as HTMLElement;
    if (!textElement || textElement.scrollWidth <= textElement.clientWidth) return;
    const popover = await this.popoverCtrl.create({
      component: IndSimpleTooltipComponent,
      componentProps: { text: textElement.textContent?.trim() },
      // Ionic anchors to event.target, which can be an inner span overflowing the clipped box.
      event: new CustomEvent('glanceTextTap', { detail: { ionShadowTarget: textElement } }),
      showBackdrop: false,
    });
    await popover.present();
  }

  // The directive toggles outside change detection; OnPush would repaint it only on the next event.
  onTooltipVisibilityChange() {
    this.cd.markForCheck();
  }

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }

  trackByLabel(index: number, field: { label: string }) { return field?.label || index; }
}
